import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Nav from './components/sub-components/Nav';
import Analysis from './pages/Analysis';
import History from './pages/History';
import HomeScreen from './pages/HomeScreen';
import Settings from './pages/Settings';
import Hilfe from './pages/Hilfe';
import AddTasks from './pages/settings/AddTasks';
import { useToast } from '../src/components/Toast';
import { fetchUserData } from './slice/userDataSlice';
import { fetchOrganizationColor } from './slice/themeSlice';
import Users from './pages/Users';
import Chat from './pages/Chat';
import ThemeProvider from './components/ThemeProvider';
import './App.css';
import './styles/dynamic-theme.css';

const PrivateRoute = (props) => {
    const { children } = props;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
  
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('email', params.get('email'));
      localStorage.setItem('name', params.get('name'));
      localStorage.setItem('id', params.get('id'));
      localStorage.setItem('language', "de");
      
      window.location.replace('/');
      return null;
    }
  
    if (!localStorage.getItem('token')) {
      window.location.href = '/api/microsoft/login';
      return null;
    }
  
    return <>{children}</>;
  };

// Role-based route wrapper
const RoleBasedRoute = ({ children, allowedRoles = null, ...props }) => {
  const { data: userData, loading } = useSelector((state) => state.admin);
  const navigate = useNavigate();
  const location = useLocation();

  // If still loading, show nothing
  if (loading) {
    return null;
  }

  // If user has "No License" role, only allow access to hilfe page
  if (userData?.user_role === 'No License') {
    // Always redirect to hilfe for "No License" users, except when already on hilfe
    if (location.pathname !== '/hilfe') {
      navigate('/hilfe', { replace: true });
      return null;
    }
    // Only render children if on hilfe page
    return <>{children}</>;
  }

  // For other roles, check if they have access to this route
  if (allowedRoles && !allowedRoles.includes(userData?.user_role)) {
    navigate('/hilfe', { replace: true });
    return null;
  }

  return <>{children}</>;
};

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { ToastContainer } = useToast();
  const { data: userData, loading } = useSelector((state) => state.admin);

  useEffect(() => {
    const email = localStorage.getItem('email');
    if (email) {
      dispatch(fetchUserData(email))
        .unwrap()
        .catch((error) => {
          console.error('Failed to fetch user data:', error);
        });
      
      // Fetch organization color for theming
      dispatch(fetchOrganizationColor(email))
        .unwrap()
        .catch((error) => {
          console.error('Failed to fetch organization color:', error);
        });
    }
  }, [dispatch]);

  // Role-based routing effect
  useEffect(() => {
    // Only redirect if user data is loaded and user has "No License" role
    if (!loading && userData?.user_role === 'No License') {
      // Define protected routes that "No License" users cannot access
      const protectedRoutes = ['/', '/analysis', '/history', '/settings', '/admin', '/chat'];
      const currentPath = location.pathname;
      
      // Check if current path is a protected route
      const isProtectedRoute = protectedRoutes.some(route => 
        currentPath === route || currentPath.startsWith(route)
      );
      
      if (isProtectedRoute) {
        navigate('/hilfe', { replace: true });
      }
    }
  }, [userData, loading, location.pathname, navigate]);

  // Show loading state while checking user role
  if (loading) {
    return (
      <ThemeProvider>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-custom-yellow"></div>
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <div>
        <ToastContainer />
      
    <div className='h-[50px]'>  <Nav /></div>
        <Routes>
          <Route path="/" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <HomeScreen />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/analysis/:historyId" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Analysis />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/history" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <History />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/settings" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                <Settings />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/settings/add" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={['ADMIN', 'SUPER_ADMIN']}>
                <AddTasks />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/admin" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={['SUPER_ADMIN']}>
                <Users />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/chat" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Chat />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/chat/:id" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Chat />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/hilfe" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Hilfe />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          {/* Add other routes as needed */}
        </Routes>
      </div>
    </ThemeProvider>
  );
}

export default App;
